'use client';

import { c as _c } from "react/compiler-runtime";
import { ProcessingStatusIndicator } from '@/components/ProcessingStatusIndicator';
import { AnalysedDataEndDateIndicator } from '@/components/analysed-data-end-date-indicator';
import { AppShellAdminPanelButton } from '@/components/layout/dashboard/app-shell-admin-panel';
import { APP_SHELL_TOOLBAR_ROW_CLASS, AppShellToolbarSeparator } from '@/comps/tabs/TableHeaderToolsContainer';
import { useTourContext } from '@/domains/tour/TourContext';
import { APP_SHELL_FOOTER_TUTORIAL_SLOT_ID } from '@/domains/tour/shell-tour-steps';
import { useShellTourOptions } from '@/domains/tour/use-shell-tour-options';
import { useAuthContext } from '@/hooks/use-auth';
import { cn } from '@/utils/shad';
import { WandSparkles } from 'lucide-react';
import { FC, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';
export { AppShellLeftRail, APP_SHELL_LEFT_RAIL_CLASS } from './app-shell-left-rail';
export { SharedLinkHeader } from './shared-link-header';
const FOOTER_ACTION_CLASS = 'flex h-7 items-center gap-1.5 text-xs font-medium text-muted-foreground transition-colors hover:text-foreground disabled:pointer-events-none disabled:opacity-50';

/** h-12 footer row + 1px separator + 12px gap — keeps floating CTAs above the app shell footer. */
export const APP_SHELL_FOOTER_CLEARANCE = 'calc(3rem + 1px + 0.75rem)';

/** Portal target for overlays that should align with the main content column (not the left rail). */
export const APP_SHELL_MAIN_PORTAL_ID = 'app-shell-main-portal';
export function AppShellFooterTutorialButton() {
  const $ = _c(12);
  const {
    startTour
  } = useTourContext();
  const shellTourOptions = useShellTourOptions();
  const [slot, setSlot] = useState(null);
  let t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = () => {
      setSlot(document.getElementById(APP_SHELL_FOOTER_TUTORIAL_SLOT_ID));
    };
    t1 = [];
    $[0] = t0;
    $[1] = t1;
  } else {
    t0 = $[0];
    t1 = $[1];
  }
  useLayoutEffect(t0, t1);
  if (!slot) {
    return null;
  }
  let t2;
  if ($[2] !== shellTourOptions || $[3] !== startTour) {
    t2 = () => shellTourOptions && startTour(shellTourOptions);
    $[2] = shellTourOptions;
    $[3] = startTour;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  const t3 = !shellTourOptions;
  let t4;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <WandSparkles className="h-3 w-3" aria-hidden={true} />;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  let t5;
  if ($[6] !== t2 || $[7] !== t3) {
    t5 = <button type="button" className={FOOTER_ACTION_CLASS} aria-label="Start product tour" onClick={t2} disabled={t3}>{t4}Tutorial</button>;
    $[6] = t2;
    $[7] = t3;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  let t6;
  if ($[9] !== slot || $[10] !== t5) {
    t6 = createPortal(t5, slot);
    $[9] = slot;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  return t6;
}
export const AppShellFooter: FC = () => {
  const $ = _c(4);
  const {
    isSharedLink
  } = useAuthContext();
  if (isSharedLink) {
    return null;
  }
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <AppShellToolbarSeparator />;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  let t1;
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = cn(APP_SHELL_TOOLBAR_ROW_CLASS, "w-full shrink-0 justify-between bg-background px-4");
    t2 = <AppShellAdminPanelButton />;
    $[1] = t1;
    $[2] = t2;
  } else {
    t1 = $[1];
    t2 = $[2];
  }
  let t3;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <>{t0}<footer aria-label="App shell footer" className={t1}>{t2}<div className="flex min-w-0 flex-wrap items-center justify-end gap-x-3 gap-y-1 sm:gap-x-5"><AnalysedDataEndDateIndicator /><ProcessingStatusIndicator showLabel={true} className="h-7 gap-1.5 text-xs" /><div id={APP_SHELL_FOOTER_TUTORIAL_SLOT_ID} /></div></footer></>;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  return t3;
};